import React, { useEffect, useState } from "react";
import { Phone, MessageCircle, Menu, X, Truck } from "lucide-react";
import { BRAND, telHref, waHref } from "../lib/site";

const NAV_ITEMS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why-us" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Quote", href: "#quote" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setMobileOpen(false);

  return (
    <>
      {/* Top utility strip */}
      <div className="hidden md:block bg-[#0F172A] text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
          <span className="text-slate-300">{BRAND.address}</span>
          <div className="flex items-center gap-5">
            <a href={telHref} data-testid="topbar-call" className="flex items-center gap-1.5 hover:text-orange-400 transition">
              <Phone size={14} /> {BRAND.phone}
            </a>
            <a href={waHref()} target="_blank" rel="noreferrer" data-testid="topbar-whatsapp" className="flex items-center gap-1.5 hover:text-green-400 transition">
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
        </div>
      </div>

      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-all ${
          scrolled ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200" : "bg-white border-b border-slate-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
          <a href="#home" data-testid="logo" className="flex items-center gap-2 group">
            <span className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-blue-700 to-blue-900 grid place-items-center shadow-md group-hover:scale-105 transition">
              <Truck className="text-white" size={20} />
              <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-orange-500 rounded-full border-2 border-white" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-extrabold text-[15px] sm:text-base text-slate-900 tracking-tight" style={{ fontFamily: "Outfit" }}>
                A SEVEN
              </span>
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-orange-600 font-semibold">
                Packers &amp; Movers
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-blue-800 hover:bg-blue-50 rounded-md transition"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={telHref}
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold shadow-sm hover:shadow-md transition"
            >
              <Phone size={15} /> Call {BRAND.phone}
            </a>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-md hover:bg-slate-100"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={close}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-3 py-2 rounded-md text-slate-800 hover:bg-blue-50"
                >
                  {item.label}
                </a>
              ))}
              <a
                href={telHref}
                onClick={close}
                className="mt-2 px-4 py-3 bg-orange-600 text-white rounded-md font-semibold text-center"
                data-testid="mobile-call-btn"
              >
                Call {BRAND.phone}
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
